import { useCallback, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type FeedbackVote = "up" | "down" | "pending";
export type FeedbackSource = "site" | "admin_preview";

const CUSTOMER_KEY_STORAGE = "deal_feedback_customer_key";

function getCustomerKey(): string {
  if (typeof window === "undefined") return "server";
  const existing = window.localStorage.getItem(CUSTOMER_KEY_STORAGE);
  if (existing) return existing;
  const generated = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `anon_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  window.localStorage.setItem(CUSTOMER_KEY_STORAGE, generated);
  return generated;
}

interface FeedbackSummary {
  up_count: number;
  down_count: number;
  total_votes: number;
  sentiment_score: number;
}

interface UseDealFeedbackArgs {
  dealDedupeKey?: string | null;
  dealId?: string | null;
  source?: FeedbackSource;
  environment?: "publish" | "staging";
  enabled?: boolean;
}

export function useDealFeedback({
  dealDedupeKey,
  dealId,
  source = "site",
  environment = "publish",
  enabled = true,
}: UseDealFeedbackArgs) {
  const [value, setValue] = useState<FeedbackVote>("pending");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);

  const canSubmit = useMemo(
    () => enabled && Boolean(dealDedupeKey && dealDedupeKey.trim().length > 0),
    [enabled, dealDedupeKey]
  );

  const submitVote = useCallback(async (vote: FeedbackVote) => {
    if (!canSubmit || !dealDedupeKey) {
      setValue(vote);
      return;
    }

    setLoading(true);
    setError(null);
    const previous = value;
    setValue(vote);

    const customerKey = getCustomerKey();
    const { data, error: fnError } = await supabase.functions.invoke("deal-feedback", {
      body: {
        environment,
        deal_dedupe_key: dealDedupeKey,
        deal_id: dealId ?? null,
        customer_key: customerKey,
        vote,
        source,
      },
    });

    if (fnError) {
      setValue(previous);
      setError(fnError.message || "Failed to submit feedback");
      setLoading(false);
      throw fnError;
    }

    if (data?.current_vote) {
      setValue(data.current_vote as FeedbackVote);
    }

    if (data?.summary) {
      setSummary(data.summary as FeedbackSummary);
    }

    setLoading(false);
  }, [canSubmit, dealDedupeKey, dealId, environment, source, value]);

  return {
    value,
    loading,
    error,
    summary,
    canSubmit,
    submitVote,
  };
}

export default useDealFeedback;
